/* eslint-disable no-unused-vars */
import {
 useContext, useEffect
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
 Combobox
} from 'shared/components/ui/combobox';
import {
 selectedComboboxGetter
} from 'builders/builderOpcode/helper/getterHelper';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import StepControllers from '../components/StepControllers';
import {
    RAGStep, RagOperation
} from 'builders/builderOpcode/jsonMode/types';

const V2StepRAG = ({
    step,
    onStepChange,
    oelContext,
}: {
    step: RAGStep;
    onStepChange: (step: RAGStep) => void;
    oelContext: OELContext;
}) => {
    const {
 collections, fetchCollections
} = useContext(SubOrgContext) as any;

    useEffect(() => {
        fetchCollections();
    }, []);

    const handleOperationChange = (operation: RagOperation) => {
        let inputs: RAGStep['inputs'];
        switch (operation) {
            case 'basic_search':
                inputs = {
                    collection_id: (step.inputs as any)?.collection_id || '',
                    query: (step.inputs as any)?.query || '',
                    top_k: (step.inputs as any)?.top_k || '10',
                    top_n: (step.inputs as any)?.top_n || '3',
                    use_rerank: (step.inputs as any)?.use_rerank ?? 'false',
                };
                break;
            case 'search_with_files':
                inputs = {
                    collection_id: (step.inputs as any)?.collection_id || '',
                    query: (step.inputs as any)?.query || '',
                    top_k: (step.inputs as any)?.top_k || '10',
                    top_n: (step.inputs as any)?.top_n || '3',
                    use_rerank: (step.inputs as any)?.use_rerank ?? 'false',
                    file_keys: (step.inputs as any)?.file_keys || '',
                };
                break;
            case 'retrieve_files':
                inputs = {
                    collection_id: (step.inputs as any)?.collection_id || '',
                    file_keys: (step.inputs as any)?.file_keys || '',
                    page_size: (step.inputs as any)?.page_size || '',
                };
                break;
        }
        onStepChange({
 ...step,
operation,
inputs
});
    };

    const updateInput = (key: string, value: string) => {
        onStepChange({
            ...step,
            inputs: {
 ...step.inputs,
[key]: value
} as RAGStep['inputs']
        });
    };

    const collectionItems = (collections || []).map((c: any) => ({
        _id: c._id || c.collection_id || c.name,
        name: c.name || c._id
    }));

    const rawCollectionId = (step.inputs as any)?.collection_id || '';
    // Strip OEL single quotes for combobox matching
    const bareCollectionId = rawCollectionId.replace(/^'(.*)'$/, '$1');

    return (
        <div className="space-y-4">
            {/* Operation */}
            <div className="space-y-1">
                <Label>Operation <span className="text-destructive">*</span></Label>
                <Select
                    value={step.operation}
                    onValueChange={(val) => handleOperationChange(val as RagOperation)}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select operation" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="basic_search">Basic Search</SelectItem>
                        <SelectItem value="search_with_files">Search with Files</SelectItem>
                        <SelectItem value="retrieve_files">Retrieve Files</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Collection ID */}
            <div className="space-y-1">
                <Label>Collection <span className="text-destructive">*</span></Label>
                {collectionItems.length > 0 ? (
                    <Combobox
                        items={collectionItems}
                        selectedValue={selectedComboboxGetter(bareCollectionId, collectionItems)}
                        setter={(value: any) => updateInput('collection_id', value?._id ? `'${value._id}'` : '')}
                        comboboxWidth="w-full"
                        defaultDisplayed="Select Collection"
                    />
                ) : (
                    <OELCodeEditor
                        value={rawCollectionId}
                        onChange={(val) => {
                            const stored =
                                val === '' || val.startsWith('@') || val.startsWith("'") || val.startsWith('"')
                                    ? val
                                    : `'${val}'`;
                            updateInput('collection_id', stored);
                        }}
                        placeholder="'collection_id' or type @ for references"
                        oelContext={oelContext}
                    />
                )}
            </div>

            {/* Search-specific fields */}
            {step.operation !== 'retrieve_files' && (
                <>
                    <OELCodeEditor
                        label="Query"
                        value={(step.inputs as any)?.query || ''}
                        onChange={(val) => updateInput('query', val)}
                        placeholder="Search query — type @ for references"
                        required
                        multiline
                        oelContext={oelContext}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <OELCodeEditor
                            label="Top K"
                            value={String((step.inputs as any)?.top_k || '10')}
                            onChange={(val) => updateInput('top_k', val)}
                            oelContext={oelContext}
                            templateMode={false}
                        />
                        <OELCodeEditor
                            label="Top N"
                            value={String((step.inputs as any)?.top_n || '3')}
                            onChange={(val) => updateInput('top_n', val)}
                            oelContext={oelContext}
                            templateMode={false}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <Switch
                            checked={(step.inputs as any)?.use_rerank === 'true' || (step.inputs as any)?.use_rerank === true}
                            onCheckedChange={(checked) => updateInput('use_rerank', String(checked))}
                        />
                        <Label>Use Rerank</Label>
                    </div>
                </>
            )}

            {/* File keys (for search_with_files and retrieve_files) */}
            {(step.operation === 'search_with_files' || step.operation === 'retrieve_files') && (
                <OELCodeEditor
                    label="File Keys"
                    value={(step.inputs as any)?.file_keys || ''}
                    onChange={(val) => updateInput('file_keys', val)}
                    placeholder="File keys — type @ for references"
                    required
                    oelContext={oelContext}
                />
            )}

            {/* Page size (retrieve_files only) */}
            {step.operation === 'retrieve_files' && (
                <OELCodeEditor
                    label="Page Size"
                    value={(step.inputs as any)?.page_size || ''}
                    onChange={(val) => updateInput('page_size', val)}
                    placeholder="Page size (optional)"
                    oelContext={oelContext}
                />
            )}

            {/* Controllers */}
            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={oelContext}
            />
        </div>
    );
};

export default V2StepRAG;
